<!-- Devices — pick the pager you carry (IDEATION → Key screens 5).
     Six cards, each a small picture of the device (DeviceThumb). Tapping one
     makes it your pager, plays its beep once (respecting ALERT) and goes back —
     the Pager screen then slides the new device in. Below: the ALERT switch,
     drawn as a pager's three-position slide switch, with radio semantics. -->
<template>
  <IonPage>
    <AppHeader title="Devices" back="/tabs/pager" />

    <IonContent :fullscreen="true">
      <div class="pm-devs">
        <p class="pm-devs__lede">Every page you have re-draws itself on the one you pick.</p>

        <ul class="pm-devs__list" role="list">
          <li v-for="d in DEVICES" :key="d.id">
            <button
              type="button"
              class="pm-devs__card"
              :class="{ 'is-in-use': d.id === settings.device }"
              :aria-current="d.id === settings.device ? 'true' : undefined"
              :aria-disabled="d.locked ? 'true' : undefined"
              @click="choose(d)"
            >
              <DeviceThumb :device-id="d.id" />
              <span class="pm-devs__info">
                <span class="pm-devs__title">
                  <span class="pm-devs__name">{{ d.name }}</span>
                  <span class="pm-devs__year">{{ d.year }}</span>
                </span>
                <span class="pm-devs__spec">{{ d.spec }}</span>
                <span class="pm-devs__blurb">{{ d.blurb }}</span>
                <span v-if="d.id === settings.device" class="pm-devs__inuse">IN USE</span>
                <span v-else-if="d.locked" class="pm-devs__locked">LOCKED</span>
              </span>
            </button>
          </li>
        </ul>

        <section class="pm-alert" aria-labelledby="pm-alert-h">
          <h2 id="pm-alert-h" class="section-title">Alert</h2>
          <p id="pm-alert-d" class="pm-alert__note">{{ alertNote }}</p>
          <div
            class="pm-slide"
            role="radiogroup"
            aria-labelledby="pm-alert-h"
            aria-describedby="pm-alert-d"
            :style="{ '--pos': alertIndex }"
          >
            <span class="pm-slide__track" aria-hidden="true">
              <span class="pm-slide__thumb" data-motion />
            </span>
            <label
              v-for="(m, i) in MODES"
              :key="m.id"
              class="pm-slide__opt"
              :class="{ 'is-on': settings.alertMode === m.id }"
              :style="{ gridColumn: i + 1 }"
            >
              <input
                class="pm-slide__input"
                type="radio"
                name="pm-alert-mode"
                :value="m.id"
                :checked="settings.alertMode === m.id"
                @change="setAlert(m.id)"
              />
              <span class="pm-slide__silk">{{ m.silk }}</span>
              <span class="sr-only">{{ m.word }}</span>
            </label>
          </div>
        </section>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { IonPage, IonContent, useIonRouter } from '@ionic/vue';
import { AppHeader } from '../core/ui';
import { haptic } from '../core/util.js';
import DeviceThumb from '../components/DeviceThumb.vue';
import { DEVICES } from '../pm/devices.js';
import { playBeep } from '../pm/beep.js';
import { settings, setDevice, setAlertMode } from '../store.js';
import { artFor } from '../pm/device-art.js';

// The full-size renders, fetched while you look at the list: the Pager screen
// slides the new device in the moment you pick one, and an image still on the
// wire made that an empty stage for a beat.
const warm = new Set();
function preload(id) {
  const src = artFor(id).src;
  if (warm.has(src)) return;
  warm.add(src);
  const img = new window.Image();
  img.decoding = 'async';
  img.src = src;
}
onMounted(() => {
  const go = () => DEVICES.forEach((d) => preload(d.id));
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(go, { timeout: 1500 });
  else setTimeout(go, 300);
});

const ionRouter = useIonRouter();

const MODES = [
  { id: 'beep', silk: 'BEEP', word: 'Beep' },
  { id: 'vibrate', silk: 'VIBRATE', word: 'Vibrate' },
  { id: 'silent', silk: 'SILENT', word: 'Silent' },
];
const alertIndex = computed(() => Math.max(0, MODES.findIndex((m) => m.id === settings.alertMode)));
const alertNote = computed(() => ({
  beep: 'New pages beep and buzz.',
  vibrate: 'New pages buzz, no sound.',
  silent: 'New pages arrive quietly. The backlight still flashes.',
}[settings.alertMode] || ''));

/** The device's own beep, once — or its buzz, or nothing — per ALERT. */
function sample(device) {
  if (settings.alertMode === 'beep') { haptic('pop'); playBeep(device.beep); }
  else if (settings.alertMode === 'vibrate') haptic('heavy');
  else haptic('tick');
}

function choose(d) {
  if (d.locked) return;
  setDevice(d.id);
  sample(d);
  if (ionRouter.canGoBack()) ionRouter.back();
  else ionRouter.navigate('/tabs/pager', 'back', 'replace');
}

function setAlert(mode) {
  setAlertMode(mode);
  if (mode === 'beep') playBeep('blip');
  else if (mode === 'vibrate') haptic('heavy');
  else haptic('tick');
}
</script>
